export type EhcpIndication = 'strength' | 'emerging' | 'need';
export type TextSource = 'original' | 'ai_rewrite';

export interface EhcpTag {
  area: string;            // taxonomy slug, e.g. 'communication-interaction.expressive-language'
  indication: EhcpIndication;
}

export type BehaviourFunction = 'escape' | 'attention' | 'tangible' | 'sensory';

export interface AbcEvent {
  antecedent: string;
  behavior: string;
  consequence: string;
  valence: string;
  // FBA function — the need the behaviour served (any valence). '' / absent when
  // no clear function applies. Drives behaviour-over-time reporting.
  function?: BehaviourFunction | '' | null;
  // ABC antecedent trigger slug (ANTECEDENT_TAGS). '' / absent when none fits, and
  // absent on episodes enriched before the trigger taxonomy shipped (no backfill) —
  // treat missing as unclassified. Drives the Behaviour Insights trigger charts.
  antecedent_tag?: string | null;
}

// The AI's enrichment output, cached server-side in ai.post_ai_metadata and
// returned with each post. The review screen reads professional_summary (the
// AI rewrite) and ehcp (the suggested tags) from here.
export interface AiMetadata {
  ehcp: EhcpTag[];
  safeguarding: string[];
  abc: AbcEvent[];
  staff_action: string[];
  other_keywords: string[];
  professional_summary: string;
}

export interface Post {
  id: number;
  post_date: string; // ISO format date string (e.g., "2025-06-21")
  title: string;
  created_by: number;
  created_by_name?: string | null;
  created_at: string; // ISO timestamp string (e.g., "2025-06-21T12:34:56Z")
  updated_at: string;
  text_content?: string | null;
  classroom_id?: number | null;
  is_published: boolean; // indicates if the post is ready to be viewed on the portal
  uploads: Upload[];
  child_ids: number[];

  // --- Review & publish (populated when the post is published) ---
  published_text?: string | null;          // frozen public-record wording (sanitised; desanitised at render)
  published_text_source?: TextSource | null;
  published_source_edited?: boolean | null;
  published_categories?: EhcpTag[] | null;  // human-approved final tags
  published_at?: string | null;
  published_by?: number | null;
  published_by_name?: string | null;
  updated_by?: number | null;               // last editor — concurrency + attribution

  // Deletion grace period (migration 041). Populated only in the deleted view.
  deleted_at?: string | null;               // when it was deleted — the countdown starts here
  deleted_by?: number | null;
  deleted_by_name?: string | null;

  // --- AI suggestions (read-only; drives the review screen) ---
  ai_metadata?: AiMetadata | null;
  taxonomy_version?: number | null;
}

// Deletion grace period — present on posts fetched with deletedOnly.
// deleted_at is what the restore countdown is measured from.

export interface Upload {
  id: number;
  file_name: string;
  file_type: string;
  s3_key: string;
  s3_key_thumbnail: string;
  upload_type_id: number;
  uploaded_at: string;
  url: string;               // full-size image (the DISPLAYED variant — modified if active, else original)
  thumbnail_url: string;     // thumbnail image (the DISPLAYED variant)
  to_delete?: boolean;       // client-side flag for deletion

  // ── Modified-image variant (face-blur / redaction) ──
  modified_s3_key?: string | null;            // redacted full-size key; null = no modified variant
  modified_s3_key_thumbnail?: string | null;  // redacted thumbnail key
  display_modified?: boolean;                  // when a modified exists: show modified (true) or original (false)

  // ── Report exclusion (reviewer decision, stored on the post↔upload link) ──
  excluded_from_report?: boolean;              // TRUE = the report/PDF must not render this image
  exclusion_reason?: string | null;            // slug from EXCLUSION_REASON_LABELS
  exclusion_note?: string | null;              // optional reviewer note

  // Read-side conveniences from the API (groupPostsWithUploads):
  has_modified?: boolean;
  original_url?: string;                       // presigned URL for the ORIGINAL full-size image
  original_thumbnail_url?: string;             // presigned URL for the ORIGINAL thumbnail
  modified_url?: string;                       // presigned URL for the MODIFIED full-size image (if any)
  modified_thumbnail_url?: string;             // presigned URL for the MODIFIED thumbnail (if any)
}

// Fixed reason list for excluding a photo from the report. Slugs are what's
// stored in post_upload.exclusion_reason; anything more nuanced goes in the
// free-text exclusion_note.
export const EXCLUSION_REASON_LABELS: Record<string, string> = {
  'similar-photo':   'Similar to another photo',
  'other-child':     'Another child identifiable',
  'not-appropriate': 'Not appropriate',
  'poor-quality':    'Poor quality',
  // The escape hatch, so a reviewer never has to force a bad category fit onto
  // a record other staff will read. The UI requires a note alongside it — an
  // exclusion reading only "Other" would say less than no reason at all.
  'other':           'Other',
};

/** Reason slug that carries no meaning on its own and needs the note filled. */
export const EXCLUSION_REASON_NEEDS_NOTE = 'other';
// ── Deleted-but-restorable ──────────────────────────────────────────────────
// Deleting a post or an image marks it and starts a 7-day clock; a scheduled
// sweep then destroys the S3 files and the database rows together. These are
// the shapes the restore screens work with — everything they describe still
// exists and can be brought back until restorable_until passes.

export interface DeletedPost {
  id: number;
  title: string;
  post_date: string;
  text_content: string | null;
  is_published: boolean;
  deleted_at: string;
  deleted_by: number | null;
  deleted_by_name: string | null;
  /** when it stops being restorable and is destroyed for good */
  restorable_until: string;
  image_count: number;
}

export interface DeletedUpload {
  id: number;
  file_name: string;
  uploaded_at: string;
  deleted_at: string;
  deleted_by_name: string | null;
  restorable_until: string;
  /** the DISPLAYED variant — blurred where a blur exists, never the raw original */
  url: string;
  thumbnail_url: string;
}

export interface RestorePostResult {
  message: string;
  post: Post;
  /** Images deleted separately run their own clock and are NOT restored with
   *  the post. Non-zero means the post came back with gaps, and the user needs
   *  telling rather than discovering it later. */
  imagesStillDeleted: number;
  note?: string;
}
